import { Injectable, inject } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import {
  AddDateFilterToFilterTransactionHistoryAction,
  AddPriceToFilterTransactionHistoriesAction,
  AddStatusToFilterTransactionHistoryAction,
  ApplyPromoCode,
  FilterTransactionAction,
  FilterTransactionSuccessAction,
  LoadTransactionHistory,
  PaymentError,
  PaymentSuccess,
  PayWithBalance,
  ProcessPayment,
  RemoveDateFilterToFilterTransactionHistoryAction,
  RemovePriceToFilterTransactionHistoriesAction,
  RemovePromoCode,
  RemoveStatusToFilterTransactionHistoryAction,
  ResetPaymentState,
  SelectPaymentMethod,
  SetActivityData,
  TransactionHistoryResponse,
} from './payment.actions';
import { defaultPaymentStateModel, PaymentStateModel } from './payment.state.model';
import { PaymentsService, TransactionHistoryItemDto } from '@smatchy/sdk';
import { WalletService } from '../../wallet/services/wallet.service';
import { tap, catchError, of, timeout, switchMap, retry, timer, throwError } from 'rxjs';
import { ShowToastMessageAction } from '../../app';
import { StripeAdapter } from '../../../../adapters/stripe.adapter';
import { LanguageService } from '../../../services/language.service';
import { AnalyticsService } from '../../../services/analytics.service';
import { customerIdOf, ephemeralKeyOf, paymentErrorMessage, unwrapPayment } from './payment-response';
import { TranslateService } from '@ngx-translate/core';
import { captureException, captureMessage } from '../../../../tracing';
import { howToGroupIt, itShouldWakeSomebody, whatWentWrong } from '../domain/what-went-wrong-with-the-payment';
@State<PaymentStateModel>({
  name: 'payment',
  defaults: defaultPaymentStateModel,
})
@Injectable()
export class PaymentState {
  private readonly _stripeAdapter = inject(StripeAdapter);
  private readonly _languageService = inject(LanguageService);
  private readonly _translate = inject(TranslateService);
  private readonly _analytics = inject(AnalyticsService);
  private readonly _walletService = inject(WalletService);

  constructor(private readonly _paymentsService: PaymentsService) {}

  @Action(PayWithBalance)
  payWithBalance({ patchState, dispatch }: StateContext<PaymentStateModel>, { payload }: PayWithBalance) {
    // Paying from the balance is synchronous server-side: the deposit either
    // completes or is refused. Success and error reuse the same two actions the
    // card flow ends on, so the screen's confetti/toast handling is unchanged.
    patchState({ isProcessing: true, paymentError: null, paymentSuccess: false, outcomeLogged: false });
    return this._walletService.payFromBalance(payload.eventId).pipe(
      tap((res) => {
        dispatch(new PaymentSuccess({ transactionId: res?.transactionId ?? '' }));
      }),
      catchError((error) => {
        const message =
          typeof error?.error?.message === 'string'
            ? error.error.message
            : this._translate.instant('MY_BALANCE.PAYMENT_FAILED');
        dispatch(new PaymentError({ error: message, stage: 'wallet' }));
        return of(null);
      }),
    );
  }

  @Action(SelectPaymentMethod)
  selectPaymentMethod({ patchState }: StateContext<PaymentStateModel>, { payload }: SelectPaymentMethod) {
    patchState({ selectedPaymentMethod: payload });
  }

  @Action(ApplyPromoCode)
  applyPromoCode({ patchState }: StateContext<PaymentStateModel>, { payload }: ApplyPromoCode) {
    patchState({ appliedPromoCode: payload });
  }

  @Action(RemovePromoCode)
  removePromoCode({ patchState }: StateContext<PaymentStateModel>) {
    patchState({ appliedPromoCode: null });
  }

  @Action(SetActivityData)
  setActivityData({ patchState }: StateContext<PaymentStateModel>, { payload }: SetActivityData) {
    patchState({
      activityData: payload,
      paymentAmount: payload.costPerParticipant,
    });
  }

  @Action(ProcessPayment)
  processPayment({ patchState, dispatch, getState }: StateContext<PaymentStateModel>, { payload }: ProcessPayment) {
    patchState({ isProcessing: true, paymentError: null, paymentSuccess: false, outcomeLogged: false });
    // The distance between this and payment_completed is the abandoned basket.
    this._analytics.logPaymentStarted({
      activityId: payload.eventId,
      amount: getState().paymentAmount,
      currency: 'EUR',
    });
    const requestDto: { eventId: string; paymentMethodId?: string } = {
      eventId: payload.eventId,
    };
    if (payload.paymentMethodId) {
      requestDto.paymentMethodId = payload.paymentMethodId;
    }

    // Set timeout: 10 seconds for ephemeral key (needs more time than payment processing)
    const EPHEMERAL_KEY_TIMEOUT_MS = 10000;
    const EPHEMERAL_KEY_ATTEMPTS = 2;
    const EPHEMERAL_KEY_RETRY_DELAY_MS = 300;
    const PAYMENT_TIMEOUT_MS = 5000;
    // Confirmation happens AFTER the card may already have been charged (3DS or
    // direct confirm). A 5 s cap on a slow-but-healthy network turned a payment
    // that actually went through into a PaymentError (the "paid-but-not-confirmed"
    // case the branches below already name). Give confirmation room to answer.
    const CONFIRM_TIMEOUT_MS = 20000;

    // If no paymentMethodId (new card), call ephemeral key endpoint first
    // Otherwise, skip ephemeral key (not needed for saved payment methods)
    const ephemeralKeyObservable = payload.paymentMethodId
      ? of(null) // Skip ephemeral key for saved payment methods
      : this._paymentsService.createEphemeralKey().pipe(
          timeout(EPHEMERAL_KEY_TIMEOUT_MS),
          // One second chance before giving up on it.
          //
          // This key is what lets the Stripe sheet show somebody the cards
          // they have already saved. Without it the sheet has nothing to
          // list and asks for a card number, in the middle of paying, which
          // is where people give up. A network blip should not cost that.
          //
          // A timeout is not retried: it already cost ten seconds, and
          // trying again would make somebody wait twice as long for the same
          // answer. Only failures that came back quickly are worth a second
          // ask.
          retry({
            count: EPHEMERAL_KEY_ATTEMPTS - 1,
            delay: (error) =>
              error?.name === 'TimeoutError' ? throwError(() => error) : timer(EPHEMERAL_KEY_RETRY_DELAY_MS),
          }),
          catchError((error) => {
            /*
             * COUNTED, not only written to a console nobody reads.
             *
             * Reaching here means the Stripe sheet opens with no saved card
             * to offer, so somebody who chose their own card is asked to
             * type one. We decided on 22 September 2026 to keep degrading
             * rather than abandon: abandoning loses the payment outright,
             * degrading loses only the convenience, and the person still
             * picks the card themselves, so nobody is charged on a card they
             * did not choose.
             *
             * That decision holds ONLY IF THIS IS RARE. Once a year is fine;
             * once in ten is a server problem hidden behind a polite
             * message, and until now nothing said which it was. This is what
             * tells them apart.
             */
            const timedOut = error?.name === 'TimeoutError';
            captureMessage('Payment sheet opened without the saved cards', {
              level: 'warning',
              tags: { ephemeral_key: timedOut ? 'timed_out' : 'failed' },
              contexts: {
                ephemeral_key: {
                  attempts: EPHEMERAL_KEY_ATTEMPTS,
                  timedOut,
                  timeoutMs: EPHEMERAL_KEY_TIMEOUT_MS,
                  reason: error?.message ?? 'unknown',
                },
              },
            });

            return of(null);
          }),
        );

    return ephemeralKeyObservable.pipe(
      switchMap((ephemeralKeyResponse) => {
        // Extract customerId and ephemeralKeySecret if available
        let customerId: string | undefined;
        let ephemeralKeySecret: string | undefined;

        if (ephemeralKeyResponse && !ephemeralKeyResponse.error) {
          const ephemeralKeyValue = unwrapPayment(ephemeralKeyResponse);
          customerId = customerIdOf(ephemeralKeyValue);
          ephemeralKeySecret = ephemeralKeyOf(ephemeralKeyValue);
        }

        // Now call process-deposit
        return this._paymentsService
          .processDeposit({
            processDepositPaymentDto: requestDto,
          })
          .pipe(
            timeout(PAYMENT_TIMEOUT_MS),
            switchMap(async (response) => {
              if (response.error) {
                console.error('[PaymentState] API returned error:', response.error);
                const errorMessage =
                  typeof response.error === 'string'
                    ? response.error
                    : paymentErrorMessage(response.error, 'Payment failed');
                dispatch(
                  new PaymentError({
                    error: errorMessage,
                  }),
                );
                return of(null);
              }

              // Wrapped in { value } or not, the rule lives in unwrapPayment.
              const responseValue = unwrapPayment(response);

              const transactionId = responseValue?.transactionId || responseValue?.id || '';
              // Use paymentIntentClientSecret (correct field name from backend)
              const clientSecret = responseValue?.paymentIntentClientSecret || responseValue?.clientSecret || '';
              const paymentStatus = responseValue?.status || responseValue?.paymentStatus || 'pending';

              // When paymentMethodId is provided and status is 'succeeded', payment is already complete
              if (payload.paymentMethodId && paymentStatus === 'succeeded') {
                // Payment already succeeded, just confirm with backend
                try {
                  const confirmResponse = await this._paymentsService
                    .confirmPayment({
                      confirmPaymentDto: { transactionId },
                    })
                    .pipe(
                      timeout(CONFIRM_TIMEOUT_MS),
                      catchError((error) => {
                        dispatch(
                          new PaymentError({
                            error: paymentErrorMessage(error, 'Payment confirmation failed'),
                          }),
                        );
                        return of(null);
                      }),
                    )
                    .toPromise();

                  if (confirmResponse?.error) {
                    const errorMessage = paymentErrorMessage(confirmResponse.error, 'Payment confirmation failed');
                    dispatch(
                      new PaymentError({
                        error: errorMessage,
                      }),
                    );
                    return of(null);
                  }

                  // Payment confirmed successfully
                  dispatch(new PaymentSuccess({ transactionId }));
                  dispatch(
                    new ShowToastMessageAction(this._translate.instant('PAY_TO_REGISTER_PAGE.PAYMENT_PROCESSED')),
                  );
                  return of(null);
                } catch (error) {
                  dispatch(
                    new PaymentError({
                      error: error instanceof Error ? error.message : 'Payment confirmation failed',
                    }),
                  );
                  return of(null);
                }
              }

              // When paymentMethodId is provided and status is 'requires_action', 3D Secure is needed.
              // Continue to the payment sheet, which uses the clientSecret to handle 3DS without
              // showing the card input.

              // When paymentMethodId is provided but status is still 'pending', try direct confirmation
              if (payload.paymentMethodId && paymentStatus === 'pending') {
                // Try to confirm payment directly - backend should have processed it
                try {
                  const confirmResponse = await this._paymentsService
                    .confirmPayment({
                      confirmPaymentDto: { transactionId },
                    })
                    .pipe(
                      timeout(CONFIRM_TIMEOUT_MS),
                      catchError((error) => {
                        console.error('[PaymentState] Direct confirmation failed, may require 3D Secure:', error);
                        // If confirmation fails, it might require 3D Secure - fall through to payment sheet
                        return of({ requiresAction: true, error: error.message });
                      }),
                    )
                    .toPromise();

                  if (confirmResponse && !unwrapPayment(confirmResponse).requiresAction && !confirmResponse?.error) {
                    // Payment confirmed successfully without payment sheet
                    dispatch(new PaymentSuccess({ transactionId }));
                    dispatch(
                      new ShowToastMessageAction(this._translate.instant('PAY_TO_REGISTER_PAGE.PAYMENT_PROCESSED')),
                    );
                    return of(null);
                  }

                  // If confirmation failed or requires action, continue to payment sheet for 3D Secure.
                } catch (error) {
                  console.error('[PaymentState] Direct confirmation error, falling back to payment sheet:', error);
                  // Fall through to payment sheet
                }
              }

              // When paymentMethodId is provided, backend should process payment server-side
              // If payment is already confirmed, skip payment sheet
              if (payload.paymentMethodId && paymentStatus === 'succeeded') {
                // Payment already succeeded, just confirm with backend
                try {
                  const confirmResponse = await this._paymentsService
                    .confirmPayment({
                      confirmPaymentDto: { transactionId },
                    })
                    .pipe(
                      timeout(CONFIRM_TIMEOUT_MS),
                      catchError((error) => {
                        dispatch(
                          new PaymentError({
                            error: paymentErrorMessage(error, 'Payment confirmation failed'),
                          }),
                        );
                        return of(null);
                      }),
                    )
                    .toPromise();

                  if (confirmResponse?.error) {
                    const errorMessage = paymentErrorMessage(confirmResponse.error, 'Payment confirmation failed');
                    dispatch(
                      new PaymentError({
                        error: errorMessage,
                      }),
                    );
                    return of(null);
                  }

                  // Payment confirmed successfully
                  dispatch(new PaymentSuccess({ transactionId }));
                  dispatch(
                    new ShowToastMessageAction(this._translate.instant('PAY_TO_REGISTER_PAGE.PAYMENT_PROCESSED')),
                  );
                  return of(null);
                } catch (error) {
                  dispatch(
                    new PaymentError({
                      error: error instanceof Error ? error.message : 'Payment confirmation failed',
                    }),
                  );
                  return of(null);
                }
              }

              // If no clientSecret is returned, it's an error (unless payment already succeeded)
              if (!clientSecret && paymentStatus !== 'succeeded') {
                console.error('[PaymentState] Missing clientSecret in response');
                dispatch(
                  new PaymentError({
                    error: 'Invalid payment response: missing client secret',
                  }),
                );
                return of(null);
              }

              // Use customerId and ephemeralKeySecret from process-deposit response if available.
              // New card: always pass so "Save Card" works. Saved card: we only open the sheet when
              // payment didn't complete (pending or requires_action). In that case always pass
              // customer config so the sheet shows card selection + 3DS, not the card input form.
              const responseCustomerId = customerIdOf(responseValue);
              const responseEphemeralKey = ephemeralKeyOf(responseValue);
              const isSavedCardFlow = !!payload.paymentMethodId;
              const finalCustomerId = responseCustomerId;
              const finalEphemeralKeySecret = responseEphemeralKey;

              if (!finalCustomerId || !finalEphemeralKeySecret) {
                if (!isSavedCardFlow) {
                  console.warn(
                    '[PaymentState] Missing customerId or ephemeralKeySecret - "Save Card" checkbox will not appear',
                  );
                } else {
                  console.warn(
                    '[PaymentState] Saved card but missing customerId/ephemeralKey - sheet may show card form instead of card selection',
                  );
                }
              }

              const locale = this._languageService.getCurrentLang();
              if (isSavedCardFlow && (paymentStatus === 'requires_action' || paymentStatus === 'pending')) {
                dispatch(
                  new ShowToastMessageAction(this._translate.instant('PAY_TO_REGISTER_PAGE.CONFIRM_CARD_WITH_BANK')),
                );
              }
              const stripeResult = await this._stripeAdapter
                .presentPaymentSheet(clientSecret, finalCustomerId, finalEphemeralKeySecret, locale)
                .catch((error) => {
                  console.error('[PaymentState] Stripe payment sheet error:', error);
                  return {
                    success: false,
                    said: 'something-else' as const,
                    error: error.message || 'Payment sheet failed',
                  };
                });

              if (!stripeResult.success) {
                dispatch(
                  new PaymentError({
                    error: stripeResult.error || 'Payment was cancelled or failed',
                    // 'cancelled or failed' was the honest name for a string
                    // that could not tell them apart. Now it can.
                    kind: whatWentWrong(stripeResult.said ?? 'something-else'),
                    stage: 'the-payment-sheet',
                  }),
                );
                return of(null);
              }

              // Payment sheet succeeded - confirm with backend
              try {
                const confirmResponse = await this._paymentsService
                  .confirmPayment({
                    confirmPaymentDto: { transactionId },
                  })
                  .pipe(
                    timeout(CONFIRM_TIMEOUT_MS),
                    catchError((error) => {
                      dispatch(
                        new PaymentError({
                          error: paymentErrorMessage(error, 'Payment confirmation failed'),
                        }),
                      );
                      return of(null);
                    }),
                  )
                  .toPromise();

                if (confirmResponse?.error) {
                  const errorMessage = paymentErrorMessage(confirmResponse.error, 'Payment confirmation failed');
                  dispatch(
                    new PaymentError({
                      error: errorMessage,
                      /*
                       * The sheet said the payment went through and we could
                       * not confirm it. Somebody has paid and is not
                       * registered for what they paid for. Nothing else in
                       * this file is as urgent as this line.
                       */
                      kind: 'paid-but-not-confirmed',
                      stage: 'confirming-after-the-sheet',
                    }),
                  );
                  return of(null);
                }

                // Payment confirmed successfully
                dispatch(
                  new PaymentSuccess({
                    transactionId,
                  }),
                );
                return of(null);
              } catch (error: unknown) {
                dispatch(
                  new PaymentError({
                    error: paymentErrorMessage(error, 'Payment confirmation failed'),
                  }),
                );
                return of(null);
              }
            }),
            catchError((error) => {
              let errorMessage = 'Payment processing failed';
              if (error.name === 'TimeoutError') {
                errorMessage = 'Payment timeout. Please try again.';
              } else if (error.message) {
                errorMessage = error.message;
              }
              dispatch(
                new PaymentError({
                  error: errorMessage,
                }),
              );
              return of(null);
            }),
          );
      }),
      catchError((error) => {
        // If ephemeral key fails, continue with payment (graceful degradation)
        console.warn('[PaymentState] Ephemeral key failed, continuing with payment:', error);
        // Continue with process-deposit even if ephemeral key failed
        return this._paymentsService
          .processDeposit({
            processDepositPaymentDto: requestDto,
          })
          .pipe(
            timeout(PAYMENT_TIMEOUT_MS),
            switchMap(async (response) => {
              // Same rule as the path above, and now literally the same code.
              const responseValue = unwrapPayment(response);

              const transactionId = responseValue?.transactionId || responseValue?.id || '';
              const clientSecret = responseValue?.paymentIntentClientSecret || responseValue?.clientSecret || '';
              const paymentStatus = responseValue?.status || responseValue?.paymentStatus || 'pending';
              const isSavedCardFlow = !!requestDto?.paymentMethodId;
              const finalCustomerId = responseValue?.customerId;
              const finalEphemeralKeySecret = responseValue?.ephemeralKeySecret;

              if (!clientSecret) {
                dispatch(
                  new PaymentError({
                    error: 'Invalid payment response: missing client secret',
                  }),
                );
                return of(null);
              }

              // This path runs because the ephemeral key call failed, so the
              // sheet often opens without the customer configuration: Stripe
              // then has no saved card to list and asks for a card number.
              // Somebody who picked their own card and is sent back to typing
              // one deserves to be told why, which is what the path above
              // already says when the bank asks for an authentication.
              if (isSavedCardFlow && (paymentStatus === 'requires_action' || paymentStatus === 'pending')) {
                dispatch(
                  new ShowToastMessageAction(this._translate.instant('PAY_TO_REGISTER_PAGE.CONFIRM_CARD_WITH_BANK')),
                );
              }

              const stripeResult = await this._stripeAdapter
                .presentPaymentSheet(
                  clientSecret,
                  finalCustomerId,
                  finalEphemeralKeySecret,
                  this._languageService.getCurrentLang(),
                )
                .catch((error) => {
                  return {
                    success: false,
                    said: 'something-else' as const,
                    error: error.message || 'Payment sheet failed',
                  };
                });

              if (!stripeResult.success) {
                dispatch(
                  new PaymentError({
                    error: stripeResult.error || 'Payment was cancelled or failed',
                    // 'cancelled or failed' was the honest name for a string
                    // that could not tell them apart. Now it can.
                    kind: whatWentWrong(stripeResult.said ?? 'something-else'),
                    stage: 'the-payment-sheet',
                  }),
                );
                return of(null);
              }

              try {
                const confirmResponse = await this._paymentsService
                  .confirmPayment({
                    confirmPaymentDto: { transactionId },
                  })
                  .pipe(
                    timeout(CONFIRM_TIMEOUT_MS),
                    catchError((error) => {
                      dispatch(
                        new PaymentError({
                          error: paymentErrorMessage(error, 'Payment confirmation failed'),
                        }),
                      );
                      return of(null);
                    }),
                  )
                  .toPromise();

                if (confirmResponse?.error) {
                  const errorMessage = paymentErrorMessage(confirmResponse.error, 'Payment confirmation failed');
                  dispatch(
                    new PaymentError({
                      error: errorMessage,
                      /*
                       * The sheet said the payment went through and we could
                       * not confirm it. Somebody has paid and is not
                       * registered for what they paid for. Nothing else in
                       * this file is as urgent as this line.
                       */
                      kind: 'paid-but-not-confirmed',
                      stage: 'confirming-after-the-sheet',
                    }),
                  );
                  return of(null);
                }

                dispatch(
                  new PaymentSuccess({
                    transactionId,
                  }),
                );
                return of(null);
              } catch (error: unknown) {
                dispatch(
                  new PaymentError({
                    error: paymentErrorMessage(error, 'Payment confirmation failed'),
                  }),
                );
                return of(null);
              }
            }),
            catchError((error) => {
              let errorMessage = 'Payment processing failed';
              if (error.name === 'TimeoutError') {
                errorMessage = 'Payment timeout. Please try again.';
              } else if (error.message) {
                errorMessage = error.message;
              }
              dispatch(
                new PaymentError({
                  error: errorMessage,
                }),
              );
              return of(null);
            }),
          );
      }),
    );
  }

  @Action(PaymentSuccess)
  paymentSuccess({ patchState, getState }: StateContext<PaymentStateModel>, { payload }: PaymentSuccess) {
    const state = getState();
    // Guarded: PaymentSuccess is dispatched from five different places in this
    // file, so one paid registration reached this handler more than once and
    // emitted payment_completed twice. Found by QC in Firebase DebugView.
    if (!state.outcomeLogged) {
      this._analytics.logPaymentCompleted({
        activityId: state.activityData?.id,
        amount: state.paymentAmount,
        currency: 'EUR',
        success: true,
      });
      // A paid registration never reaches DetailEventsState.joinEvent, which is
      // where activity_join is emitted for free activities. Without this, every
      // paid join was missing from the view-to-join rate, which is precisely the
      // number this instrumentation exists to produce.
      if (state.activityData) {
        this._analytics.logActivityJoin({
          activityId: state.activityData.id,
          sport: state.activityData.sportKeyId,
          city: state.activityData.location?.name,
          isPaid: true,
          seatsLeft: state.activityData.nbPlacesLeft,
        });
      }
    }
    patchState({
      isProcessing: false,
      paymentSuccess: true,
      transactionId: payload.transactionId,
      paymentError: null,
      outcomeLogged: true,
    });
  }

  @Action(PaymentError)
  paymentError({ patchState, getState }: StateContext<PaymentStateModel>, { payload }: PaymentError) {
    const state = getState();
    // failure_reason is what separates a technical blocker from a change of
    // mind, which is the whole reason this event carries a success flag rather
    // than only firing on success.
    //
    // Guarded for the same reason as the success path, and more urgently:
    // PaymentError is dispatched from nineteen places, so one failing payment
    // could emit this event many times over.
    if (!state.outcomeLogged) {
      this._analytics.logPaymentCompleted({
        activityId: state.activityData?.id,
        amount: state.paymentAmount,
        currency: 'EUR',
        success: false,
        failureReason: payload.error,
      });

      /*
       * AND REPORTED, not only counted.
       *
       * Analytics tells us next week that payments are failing. This tells us
       * in the next minute which one failed and why, which is the difference
       * between noticing a broken payment and noticing a trend.
       *
       * Behind the same outcomeLogged guard as the analytics event, and for
       * the same reason: this action is dispatched from nineteen places and
       * one failing payment must not arrive as nineteen reports.
       */
      const why = payload.kind ?? 'ours-to-fix';
      const stage = payload.stage ?? 'unknown';

      if (itShouldWakeSomebody(why)) {
        captureException(new Error(`A payment failed: ${payload.error}`), {
          level: why === 'paid-but-not-confirmed' ? 'fatal' : 'error',
          fingerprint: howToGroupIt(why, stage),
          tags: { payment_failure: why, payment_stage: stage },
          contexts: {
            payment: {
              activityId: state.activityData?.id,
              amount: state.paymentAmount,
              currency: 'EUR',
              reason: payload.error,
            },
          },
        });
      } else if (why !== 'abandoned') {
        // A refusal. Counted here, and the API already carries Stripe's own
        // reason for it, so this side stays quiet about the detail.
        captureMessage(`A payment failed: ${why}`, {
          level: 'info',
          fingerprint: howToGroupIt(why, stage),
          tags: { payment_failure: why, payment_stage: stage },
        });
      }
    }
    patchState({
      isProcessing: false,
      paymentError: payload.error,
      paymentSuccess: false,
      outcomeLogged: true,
    });
  }

  @Action(ResetPaymentState)
  resetPaymentState({ getState, patchState }: StateContext<PaymentStateModel>) {
    // Preserve selectedPaymentMethod when resetting state
    // This allows users to select a payment method and navigate back without losing their selection
    const currentState = getState();
    patchState({
      ...defaultPaymentStateModel,
      selectedPaymentMethod: currentState.selectedPaymentMethod, // Preserve selected payment method
    });
  }

  @Action(LoadTransactionHistory)
  loadTransactionHistory({ patchState }: StateContext<PaymentStateModel>, { payload }: LoadTransactionHistory) {
    patchState({ loadingTransactionHistory: true });
    return this._paymentsService
      .getUserTransactionHistory({
        eventId: payload.eventId,
        status: payload.status,
      })
      .pipe(
        tap((response) => {
          const transactions: TransactionHistoryItemDto[] = response.value?.transactions || [];
          patchState({
            transactionHistory: transactions,
            loadingTransactionHistory: false,
          });
        }),
        catchError((error) => {
          console.error('Failed to load transaction history:', error);
          patchState({
            transactionHistory: [],
            loadingTransactionHistory: false,
          });
          return of(null);
        }),
      );
  }
  @Action(FilterTransactionAction)
  filterTransaction({ patchState, dispatch }: StateContext<PaymentStateModel>, { filter }: FilterTransactionAction) {
    return this._paymentsService.filterTransactionByPaidEventId(filter).pipe(
      tap((response) => {
        dispatch(new FilterTransactionSuccessAction(response as TransactionHistoryResponse));
      }),
      catchError((error) => {
        console.error('Failed to load transaction history:', error);
        patchState({
          transactionHistory: [],
        });
        return of(null);
      }),
    );
  }
  @Action(FilterTransactionSuccessAction)
  filterTransactionSuccess(
    { patchState }: StateContext<PaymentStateModel>,
    { response }: FilterTransactionSuccessAction,
  ) {
    patchState({
      filterTransactions: response,
    });
  }
  @Action(AddDateFilterToFilterTransactionHistoryAction)
  async addDateFilterToFilterTransactionHistoryAction(
    { patchState }: StateContext<PaymentStateModel>,
    { dateFilter, fromDate, toDate }: AddDateFilterToFilterTransactionHistoryAction,
  ) {
    if (dateFilter === 'CUSTOM') {
      patchState({
        fromDate,
        toDate,
      });
    } else {
      patchState({
        dateFilter,
      });
    }
  }
  @Action(RemoveDateFilterToFilterTransactionHistoryAction)
  async removeDateFilterToFilterAction({ patchState }: StateContext<PaymentStateModel>) {
    patchState({
      dateFilter: null,
      fromDate: null,
      toDate: null,
    });
  }
  @Action(AddPriceToFilterTransactionHistoriesAction)
  async addPriceToFilterAction(
    { patchState }: StateContext<PaymentStateModel>,
    { priceFrom, priceTo }: AddPriceToFilterTransactionHistoriesAction,
  ) {
    patchState({
      priceFrom,
      priceTo,
      pricingChanged: true,
    });
  }
  @Action(RemovePriceToFilterTransactionHistoriesAction)
  async removePriceToFilterAction({ patchState }: StateContext<PaymentStateModel>) {
    patchState({
      priceFrom: 0,
      priceTo: 10000,
      pricingChanged: false,
    });
  }
  @Action(AddStatusToFilterTransactionHistoryAction)
  async addStatusToFilterAction(
    { patchState, getState }: StateContext<PaymentStateModel>,
    { status }: AddStatusToFilterTransactionHistoryAction,
  ) {
    patchState({
      statuses: [...new Set([...getState().statuses, status])],
    });
  }
  @Action(RemoveStatusToFilterTransactionHistoryAction)
  async removeStatusToFilterAction(
    { patchState, getState }: StateContext<PaymentStateModel>,
    { status }: RemoveStatusToFilterTransactionHistoryAction,
  ) {
    patchState({
      statuses: [...getState().statuses.filter((s) => s !== status)],
    });
  }
}
